import { Button } from '@affine/component';
import {
  erpSiteScope$,
  facilityForWorkspace,
  setScopeFacility,
} from '@affine/core/modules/erp/erp-site-scope';
import { ErpService } from '@affine/core/modules/erp/services/erp';
// pipe (site scope): the runtime boundary every ERP route mounts under. Two jobs:
//
// 1. SYNC — resolve the ACTIVE workspace's facility from the server's provisioning map and pin
//    it into the module-level scope (erp-site-scope.ts) in a LAYOUT effect, which React runs
//    BEFORE any child screen's passive fetch effect — so the very first list request already
//    carries the right `x-pipe-site-scope` header.
// 2. REMOUNT — key the subtree by the EFFECTIVE scope, so flipping "This site ⇄ All sites" (or
//    switching to a workspace bound to another site) freshly mounts the screen and every load
//    effect refetches under the new scope. A scope flip is rare; a remount is the simplest
//    correct invalidation (no per-screen dependency sweep).
//
// This file is RUNTIME (DI) — never import it from the pure `*-view.tsx` seams.
//
// It is ALSO where the app provides its ⓘ info-tip implementation to the shared @affine/erp-ui
// presenters (ErpPageHeader / TableFilterInput render through the package's ErpInfoTipSlot): the
// real ErpInfoTip reads the module-level preference store via @toeverything/infra, which the
// package must never depend on, so the ONE provider mount lives here — the boundary every ERP
// route already wraps (erpScopedLazy), covering every consumer at once.
import { useErpSiteMapping } from '@affine/core/modules/erp/use-erp-provisioning';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { ErpInfoTipProvider } from '@affine/erp-ui';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import {
  type ComponentType,
  Fragment,
  type PropsWithChildren,
  useLayoutEffect,
} from 'react';

import { ErpInfoTip } from './erp-info-tip';

export const ErpScopeBoundary = ({ children }: PropsWithChildren) => {
  const t = useI18n();
  const erp = useService(ErpService);
  const status = useLiveData(erp.provisioningStatus$);
  const workspace = useService(WorkspaceService).workspace;
  const mapping = useErpSiteMapping();
  const scope = useLiveData(erpSiteScope$);
  const facility = facilityForWorkspace(mapping, workspace.id);

  useLayoutEffect(() => {
    setScopeFacility(facility?.id ?? null);
  }, [facility?.id]);

  // Do not mount a screen in the membership union before its site map is known.
  // A revalidation failure retains the verified narrowing, not stale permissions.
  if (!mapping)
    return (
      <div role={status === 'error' ? 'alert' : 'status'}>
        {status === 'error'
          ? t['com.affine.pipe.records.load-error']()
          : t['Loading']()}
        {status === 'error' && (
          <Button
            onClick={() => {
              erp.provisioningFresh().catch(() => {});
            }}
          >
            {t['Retry']()}
          </Button>
        )}
      </div>
    );
  const key = `${mapping.userId}:${workspace.id}:${scope.allSites || !facility ? 'all-sites' : facility.id}`;
  return (
    <Fragment key={key}>
      <ErpInfoTipProvider value={ErpInfoTip}>{children}</ErpInfoTipProvider>
    </Fragment>
  );
};

/** Marks an erpScopedLazy-wrapped route loader so the route table stays introspectable —
 *  the erp-routes.spec.ts drift pin walks workbenchRoutes for this tag. */
export const ERP_SCOPED_MARKER: unique symbol = Symbol('pipe.erpScoped');

/** True when a workbench route's `lazy` was produced by erpScopedLazy (i.e. an ERP screen). */
export function isErpScopedRoute(lazy: unknown): boolean {
  return typeof lazy === 'function' && ERP_SCOPED_MARKER in lazy;
}

/** Wrap a react-router lazy ERP route module so its Component mounts under the boundary. */
export function erpScopedLazy(
  load: () => Promise<{ Component: ComponentType }>
): () => Promise<{ Component: ComponentType }> {
  const lazy = async () => {
    const { Component } = await load();
    const Scoped = () => (
      <ErpScopeBoundary>
        <Component />
      </ErpScopeBoundary>
    );
    return { Component: Scoped };
  };
  return Object.assign(lazy, { [ERP_SCOPED_MARKER]: true as const });
}
